import { useState, useEffect, useCallback } from 'react';
import {
    View,
    Text,
    SafeAreaView,
    FlatList,
    ActivityIndicator,
    TouchableOpacity,
    StyleSheet,
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useAuthUser } from "../../contexts/useAuthUser";
import { firestore } from '../../services/firebase';
import {
    collection,
    query,
    where,
    onSnapshot,
    getDoc,
    doc,
    setDoc,
} from 'firebase/firestore';
import Header from '../../components/Header';
import { SmallText, SubTitle } from '../../config/theme/globalStyles';
import THEME from '../../config/theme';
import PlayList from '../../components/PlayList';
import { useCustomTheme } from '../../contexts/useCustomTheme';
import { openClassOrLinkContent } from '../../utils/openLinkContent';

export function Favorites() {
    const [favorites, setFavorites] = useState([]);
    const [videos, setVideos] = useState([]);
    const [load, setLoad] = useState(true);
    // Sessão já resolvida no boot; esta tela usa o usuário do Auth (uid).
    const { authUser, isLogged, isLoadingUser: isLoading } = useAuthUser();
    const user = isLogged ? authUser : "";

    const navigation = useNavigation();

     const { customFont, customColor, customInfo } = useCustomTheme();

    const fetchFavorites = useCallback(async () => {
        setLoad(true);
        const q = query(
            collection(firestore, 'users', user?.uid, 'contents'),
            where('isFavorite', '==', true)
        );
        try {
            const unsubscribe = onSnapshot(q, (querySnapshot) => {
                const videoIds = querySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setFavorites(videoIds);
            });

            return () => unsubscribe();
        } catch (error) {
            console.log('Error fetching favorites:', error);
        }
    }, [user?.uid]);

    const fetchVideos = useCallback(async (favoriteList) => {
        const videos = [];
        for (const favoriteVideo of favoriteList) {
            const docRef = doc(
                firestore,
                'categories',
                favoriteVideo.categoryId,
                'cards',
                favoriteVideo.cardId,
                'videos',
                favoriteVideo.id
            );
            const videoData = await getDoc(docRef);

            if (videoData.exists()) { // Check if the document exists before accessing its data
                videos.push({
                    id: favoriteVideo.id,
                    ...videoData.data(),
                    cardId: favoriteVideo.cardId,
                    categoryId: favoriteVideo.categoryId,
                });
            } else {
                console.log("Video document not found:", favoriteVideo.id);
            }
        }
        setVideos(videos);
        setLoad(false);
        return videos;
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (!isLoading) {
                await fetchFavorites();
            }
        };
        fetchData();
    }, [fetchFavorites, isLoading]);

    useEffect(() => {
        const fetchVideosData = async () => {
            if (favorites.length > 0) {
                try {
                    await fetchVideos(favorites);
                    setLoad(false);
                } catch (error) {
                    // Handle the error properly
                    console.log("Error fetching videos:", error);
                }
            }
            setLoad(false);
        };
        fetchVideosData();
    }, [favorites, fetchVideos]);

    const handleSelectedVideoChange = (item) => {
        void openClassOrLinkContent({
            item,
            webUrl: customInfo?.webUrl,
            categoryId: item?.categoryId,
            cardId: item?.cardId,
            videoId: item?.id,
            onOpenClass: () => {
                navigation.navigate('Aula', {
                    cardId: item?.cardId,
                    categoryId: item?.categoryId,
                    selectedVideo: item?.id,
                });
            },
        });
    };

    const handleRemoveLike = async (videoId, cardId, categoryId) => {
        try {
            const contentsDocRef = doc(
                firestore,
                "users",
                user?.uid,
                "contents",
                videoId
            );
            await setDoc(contentsDocRef, {
                cardId,
                categoryId,
                isFavorite: false
            }, { merge: true });
        } catch (error) {
            console.error("Error adding document isFavorite: ", error);
        }
    };


    // Cores/tipografia DA MARCA DO CLIENTE (tema do app).
    const clientBg = customColor?.background?.app || THEME.COLORS.BACKGROUND_MAIN;
    const clientText = customColor?.text?.app || THEME.COLORS.TEXT_MAIN;
    const clientPrimary = customColor?.primary || THEME.COLORS.PRIMARY_800;
    const fontTitle = customFont?.fontFamilySubtitle || THEME.FONTFAMILY.MEDIUM;
    const fontText = customFont?.fontFamilyText || THEME.FONTFAMILY.REGULAR;

    const ItemSeparatorView = () => (
        <View style={styles.separator} />
    );

    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: clientBg }}>
        <Header
          onPress={() => {
            navigation.navigate('Conteudo', { screen: 'Aplicativo' });
          }}
          user={user?.uid}
        />

        <View style={styles.contentWrap}>
          <View style={styles.page}>
            {/* Cabeçalho */}
            <View style={styles.headerBlock}>
              <SubTitle
                style={{ fontFamily: fontTitle }}
                color={clientText}
                textAlign="left"
              >
                Meus favoritos
              </SubTitle>
              <SmallText
                style={{ fontFamily: fontText }}
                color={clientText}
                textAlign="left"
              >
                Todos os conteúdos que você adicionar como favorito irão aparecer nesta seção.
              </SmallText>
            </View>

            {load ? (
              <View style={styles.centerBox}>
                <ActivityIndicator size="large" color={clientPrimary} />
              </View>
            ) : videos.length === 0 ? (
              <View style={styles.centerBox}>
                <Feather name="heart" size={30} color={clientText} style={{ opacity: 0.45 }} />
                <Text style={{ marginTop: 12, fontFamily: fontTitle, fontSize: 16, color: clientText }}>
                  Você ainda não tem favoritos
                </Text>
                <Text style={{ marginTop: 4, fontFamily: fontText, fontSize: 13, color: clientText, opacity: 0.7, textAlign: 'center' }}>
                  Toque no coração de um conteúdo para salvá-lo aqui.
                </Text>
              </View>
            ) : (
              <FlatList
                data={videos}
                style={{ width: '100%' }}
                contentContainerStyle={{ paddingBottom: 24 }}
                ItemSeparatorComponent={ItemSeparatorView}
                keyExtractor={(item, index) => item?.id || `fav-${index}`}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={styles.itemRow}
                    onPress={() => handleSelectedVideoChange(item)}
                  >
                    <PlayList
                      {...item}
                      user={user}
                      favorites={true}
                      handleRemoveLike={handleRemoveLike}
                    ></PlayList>
                  </TouchableOpacity>
                )}
              />
            )}
          </View>
        </View>
      </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    contentWrap: {
        flex: 1,
        width: '100%',
        alignItems: 'center',
    },
    page: {
        flex: 1,
        width: '100%',
        maxWidth: 640,
        paddingHorizontal: 16,
        paddingTop: 8,
    },
    headerBlock: {
        marginTop: 8,
        marginBottom: 20,
    },
    centerBox: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 60,
        paddingHorizontal: 24,
    },
    itemRow: {
        paddingVertical: 12,
    },
    separator: {
        height: 1,
        backgroundColor: 'rgba(0,0,0,0.06)',
    },
});
